// Polyfills loaded before any heavy dependency. Targeted at iOS Safari
// builds older than what pdfjs-dist v5 / transformers.js expect. Each
// polyfill is gated on its native availability so modern browsers pay
// nothing.

type Resolvers<T> = {
  promise: Promise<T>;
  resolve: (value: T | PromiseLike<T>) => void;
  reject: (reason?: unknown) => void;
};

type PromiseWithResolversCtor = PromiseConstructor & {
  withResolvers?: <T>() => Resolvers<T>;
};

const PromiseCtor = Promise as PromiseWithResolversCtor;

if (typeof PromiseCtor.withResolvers !== 'function') {
  PromiseCtor.withResolvers = function withResolvers<T>(): Resolvers<T> {
    let resolve!: (value: T | PromiseLike<T>) => void;
    let reject!: (reason?: unknown) => void;
    const promise = new Promise<T>((res, rej) => {
      resolve = res;
      reject = rej;
    });
    return { promise, resolve, reject };
  };
}

// ReadableStream async iteration (Safari 17.5+). pdfjs uses
// `for await (const chunk of stream)` inside getTextContent on the main
// thread; iOS 18.7 (the legacy security-update branch off iOS 17.x) does
// not have Symbol.asyncIterator on ReadableStream and throws
// "undefined is not a function" when the loop tries to invoke it.
type AsyncIterableStream<R> = ReadableStream<R> & {
  [Symbol.asyncIterator]?: () => AsyncIterator<R>;
};

if (
  typeof ReadableStream !== 'undefined' &&
  typeof (ReadableStream.prototype as AsyncIterableStream<unknown>)[Symbol.asyncIterator] !==
    'function'
) {
  (ReadableStream.prototype as AsyncIterableStream<unknown>)[Symbol.asyncIterator] =
    function asyncIterator<R>(this: ReadableStream<R>): AsyncIterator<R> & AsyncIterable<R> {
      const reader = this.getReader();
      const iter: AsyncIterator<R> & AsyncIterable<R> = {
        next(): Promise<IteratorResult<R>> {
          return reader.read().then((r) => {
            if (r.done) {
              reader.releaseLock();
              return { value: undefined, done: true };
            }
            return { value: r.value, done: false };
          });
        },
        return(value?: R): Promise<IteratorResult<R>> {
          reader.releaseLock();
          return Promise.resolve({ value: value as R, done: true });
        },
        [Symbol.asyncIterator](): AsyncIterator<R> & AsyncIterable<R> {
          return iter;
        },
      };
      return iter;
    };
}
